'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { AuthCard } from './AuthCard'
import { AuthSignupCard } from './AuthSignupCard'

type AuthView = 'login' | 'signup'

export function AuthModal({
  open,
  onOpenChange,
  sellerId,
  initialView = 'login',
  companyStatus,
  companyJoinMode = 'open',
  isWhitelabel,
  communityName = '',
  communityAvatarUrl = null,
  chrome = 'glass',
  theme = 'light',
  next,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  sellerId: number
  initialView?: AuthView
  companyStatus?: any
  companyJoinMode?: 'open' | 'invite_only'
  isWhitelabel?: boolean
  communityName?: string
  communityAvatarUrl?: string | null
  chrome?: any
  theme?: any
  next?: string
  onSuccess?: () => void
}) {

  const [view, setView] = useState<AuthView>(initialView)

  // si abres el modal pidiendo signup desde afuera, respétalo
  useEffect(() => {
    if (open) setView(initialView)
  }, [open, initialView])

  const title = useMemo(() => {
    return view === 'login' ? 'Accede para continuar' : 'Crea tu cuenta'
  }, [view])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // ✅ sin "card" del dialog: solo un contenedor transparente
        className={cn(
          'border-0 bg-transparent shadow-none p-0 overflow-visible',
          // mobile: ocupa casi todo
          'w-[calc(100vw-24px)] sm:w-auto',
          'max-w-none'
        )}
      >
        <div className="w-full flex items-center justify-center">
          {/* Wrapper para que se vea centrado y consistente */}
          <div className="w-full max-w-md sm:max-w-lg">
            {/* Header minimal (no card) */}
            <div className="px-1 pb-3">
              <div className="text-sm text-muted-foreground hidden">{title}</div>

              {/* Switch pill SOLO dentro del modal */}
              <div className="mt-2 inline-flex rounded-full bg-black/5 dark:bg-white/10 p-1">
                <button
                  type="button"
                  onClick={() => setView('login')}
                  className={cn(
                    'px-3 py-1.5 text-xs rounded-full transition mr-2',
                    view === 'login'
                      ? 'bg-white dark:bg-white/15 text-foreground shadow-sm'
                      : 'bg-white/40 dark:bg-white/5 text-white hover:text-foreground hover:bg-white/60 dark:hover:bg-white/10'
                  )}
                >
                  Acceder
                </button>
                <button
                  type="button"
                  onClick={() => setView('signup')}
                  className={cn(
                    'px-3 py-1.5 text-xs rounded-full transition',
                    view === 'signup'
                      ? 'bg-white dark:bg-white/15 text-foreground shadow-sm'
                      : 'bg-white/40 dark:bg-white/5 text-white hover:text-foreground hover:bg-white/60 dark:hover:bg-white/10'
                  )}
                >
                  Crear cuenta
                </button>
              </div>
            </div>

            {/* ✅ Aquí solo renderizamos UNA tarjeta visual (AuthCard o Signup) */}
            {view === 'login' ? (
              <AuthCard
                sellerId={sellerId}
                variant="modal"
                chrome={chrome}
                theme={theme}
                next={next}
                companyStatus={companyStatus}
                isWhitelabel={isWhitelabel}
                onRequestSignup={() => setView('signup')}
                onSuccess={() => {
                  onOpenChange(false)
                  onSuccess?.()
                }}
              />
            ) : (
              <AuthSignupCard
                companyStatus={companyStatus}
                companyJoinMode={companyJoinMode || 'open'}
                isWhitelabel={isWhitelabel}
                communityName={communityName}
                communityAvatarUrl={communityAvatarUrl}
                sellerId={sellerId}
                variant="modal"
                chrome={chrome}
                theme={theme}
                next={next}
                onRequestLogin={() => setView('login')}
                onSuccess={() => {
                  onOpenChange(false)
                  onSuccess?.()
                }}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
